import { isAxiosError } from 'axios'
import { ApiError } from '@/api/http/types'
import { isNetworkApiError } from '@/shared/lib/apiErrorMessages'
/** Shown when wizard AI generation (step 1 / step 2) fails or connectivity is lost. */
const CAMPAIGN_AI_ANALYSIS_FAILED_MESSAGE =
  'AI analysis failed. Please try again.'

/** Local dev — wizard step 1/2 when AI HTTP is down or misconfigured. */
const CAMPAIGN_AI_DEV_UNAVAILABLE_HINT =
  'AI service is unavailable. Start brushfire-ai-service on port 4004 (`uvicorn main:app --reload --port 4004` in that repo), ensure AI_SERVICE_URL=http://localhost:4004 in campaign-service `.env`, then retry.'

/** Local dev — connectivity / offline during wizard AI steps. */
const CAMPAIGN_AI_DEV_CONNECTIVITY_HINT =
  'Campaign analysis could not complete. Ensure brushfire-ai-service is on port 4004 (`uvicorn main:app --reload --port 4004`), brushfire-campaign-service on :4001 (`pnpm dev`), and Sensemaker on :3000 (`pnpm dev`). Analysis can take 1–2 minutes.'

function devCampaignAiFallback(): string {
  return import.meta.env.DEV
    ? CAMPAIGN_AI_DEV_CONNECTIVITY_HINT
    : CAMPAIGN_AI_ANALYSIS_FAILED_MESSAGE
}

function messageFromUnauthorizedApiError(): string {
  return import.meta.env.DEV
    ? 'Session expired or not logged in. Log in again using the same URL as Sensemaker (e.g. http://app.localhost:3000/login).'
    : 'Please log in again.'
}

function messageFromServiceUnavailableApiError(): string {
  return import.meta.env.DEV
    ? CAMPAIGN_AI_DEV_UNAVAILABLE_HINT
    : 'AI service is temporarily unavailable. Please try again.'
}

function messageFromNetworkApiError(e: ApiError): string {
  if (import.meta.env.DEV) {
    if (/timeout/i.test(e.message)) {
      return 'Analysis took longer than the browser allowed. Ensure brushfire-ai-service (:4004) and campaign-service (:4001) are running, restart Sensemaker Vite, then retry.'
    }
    return 'Cannot reach the campaign API. Start brushfire-campaign-service on port 4001 (`pnpm dev`), brushfire-ai-service on :4004 (`uvicorn main:app --reload --port 4004`), then retry.'
  }
  return CAMPAIGN_AI_ANALYSIS_FAILED_MESSAGE
}

function messageFromApiErrorForCampaignAi(e: ApiError): string | null {
  if (e.code === 'unauthorized') {
    return messageFromUnauthorizedApiError()
  }
  if (e.code === 'service_unavailable') {
    return messageFromServiceUnavailableApiError()
  }
  if (e.code === 'network_error') {
    return messageFromNetworkApiError(e)
  }
  if (e.message) {
    return e.message
  }
  return null
}

function messageFromNetworkFailureForCampaignAi(e: unknown): string {
  if (import.meta.env.DEV && isAxiosError(e)) {
    const detail = e.code ?? e.message
    return `${CAMPAIGN_AI_DEV_CONNECTIVITY_HINT} (${detail})`
  }
  return devCampaignAiFallback()
}

/** Wizard step 1 / step 2 AI generation — API, timeout, offline, or proxy failures. */
export function messageFromCampaignAiStepError(e?: unknown): string {
  if (isAxiosError(e)) {
    const fromBody = apiErrorFromAxiosBody(e)
    if (fromBody) {
      return messageFromCampaignAiStepError(fromBody)
    }
  }
  if (e instanceof ApiError) {
    const fromApi = messageFromApiErrorForCampaignAi(e)
    if (fromApi) {
      return fromApi
    }
  }
  if (isNetworkApiError(e)) {
    return messageFromNetworkFailureForCampaignAi(e)
  }
  if (import.meta.env.DEV && e instanceof Error && e.message) {
    return `${CAMPAIGN_AI_DEV_CONNECTIVITY_HINT} (${e.message})`
  }
  return devCampaignAiFallback()
}

function apiErrorFromAxiosBody(
  e: import('axios').AxiosError,
): ApiError | null {
  const status = e.response?.status ?? 0
  const body = e.response?.data as
    | {
        message?: string
        error?: string | { code?: string; details?: unknown }
      }
    | undefined
  if (!body?.error) return null
  const nested =
    typeof body.error === 'object' &&
    body.error !== null &&
    'code' in body.error
      ? body.error
      : null
  return new ApiError(
    body.message ??
      (typeof body.error === 'string' ? body.error : 'Request failed'),
    status,
    nested?.code ?? 'unknown_error',
    null,
  )
}
